import { Moment } from "moment";
import { ModelBase } from "../../abstract/model-base.model";
import { Account } from "../account.model";
import { AllocationType } from "../allocation-type.model";
import { AllocationTypeEnum } from "src/app/@shared/enums/allocation-type.enum";
import { OmitField } from "src/app/@shared/decorators/api-field.decorator";

export abstract class AllocationBase extends ModelBase {

    // ATTRIBUTS

    private _account!: Account;
    private _aiCreated!: boolean;
    private _aiExplanation!: string;
    private _aiUserRejected!: boolean;
    private _aiUserRejectedAt!: Moment;
    private _aiUserRejectedBy!: number;
    private _aiUserValidated!: boolean;
    private _aiUserValidatedAt!: Moment;
    private _aiUserValidatedBy!: number;
    private _allocationType!: AllocationType;
    private _amount!: number;
    private _auto!: boolean;
    private _baseAmount!: number;
    private _label!: string;
    private _percentage!: number;

    // MÉTHODES

    constructor(data: any) {
        super(data);
        this._amount = this._amount || 0;
        this._baseAmount = this._baseAmount || 0;
        this._percentage = this._percentage || 0;
        this._allocationType = this._allocationType || new AllocationType({
            code: AllocationTypeEnum.Amount,
            label: "Montant"
        });
    }

    /**
     * Déduit le pourcentage du montant. Cinq décimales, comme la saisie : à deux décimales,
     * un tiers d'un montant à sept chiffres se retrouvait déplacé de plusieurs centaines
     * d'unités au premier aller-retour (#1040).
     */
    public calculatePercentage(): void {
        this.percentage = (this.amount / this.baseAmount * 100).toFixed(5);
    }

    // GETTERS / SETTERS

    public get account(): Account {
        return this._account;
    }

    public set account(value: any) {
        const hasChanged = this._setValue('_account', value, 'Account');
        if (hasChanged) {
            this.auto = false;
        }
    }

    /**
     * Trace d'audit IA — backend-only. `@OmitField` pour éviter d'écraser
     * l'état serveur lors d'un save côté front.
     */
    @OmitField()
    public get aiCreated(): boolean {
        return this._aiCreated;
    }

    public set aiCreated(value: any) {
        this._setValue('_aiCreated', value, 'boolean');
    }

    @OmitField()
    public get aiExplanation(): string {
        return this._aiExplanation;
    }

    public set aiExplanation(value: any) {
        this._setValue('_aiExplanation', value, 'string');
    }

    @OmitField()
    public get aiUserRejected(): boolean {
        return this._aiUserRejected;
    }

    public set aiUserRejected(value: any) {
        this._setValue('_aiUserRejected', value, 'boolean');
    }

    @OmitField()
    public get aiUserRejectedAt(): Moment {
        return this._aiUserRejectedAt;
    }

    public set aiUserRejectedAt(value: any) {
        this._setValue('_aiUserRejectedAt', value, 'date');
    }

    @OmitField()
    public get aiUserRejectedBy(): number {
        return this._aiUserRejectedBy;
    }

    public set aiUserRejectedBy(value: any) {
        this._setValue('_aiUserRejectedBy', value, 'integer');
    }

    @OmitField()
    public get aiUserValidated(): boolean {
        return this._aiUserValidated;
    }

    public set aiUserValidated(value: any) {
        this._setValue('_aiUserValidated', value, 'boolean');
    }

    @OmitField()
    public get aiUserValidatedAt(): Moment {
        return this._aiUserValidatedAt;
    }

    public set aiUserValidatedAt(value: any) {
        this._setValue('_aiUserValidatedAt', value, 'date');
    }

    @OmitField()
    public get aiUserValidatedBy(): number {
        return this._aiUserValidatedBy;
    }

    public set aiUserValidatedBy(value: any) {
        this._setValue('_aiUserValidatedBy', value, 'integer');
    }

    public get allocationType(): AllocationType {
        return this._allocationType;
    }

    public set allocationType(value: any) {
        const hasChanged = this._setValue('_allocationType', value, 'AllocationType');
        if (hasChanged) {
            this.auto = false;
        }
    }

    public get amount(): number {
        return this._amount;
    }

    public set amount(value: any) {
        const hasChanged = this._setValue('_amount', value, 'float');
        if (hasChanged) {
            this.auto = false;
        }
    }

    public get auto(): boolean {
        return this._auto;
    }

    public set auto(value: any) {
        this._setValue('_auto', value, 'boolean');
    }

    public get baseAmount(): number {
        return this._baseAmount;
    }

    public set baseAmount(value: any) {
        const hasChanged = this._setValue('_baseAmount', value, 'float');
        if (hasChanged) {
            this.calculatePercentage();
            this.auto = false;
        }
    }

    public get isComplete(): boolean {
        return !!this._account &&
            (this._allocationType?.code === AllocationTypeEnum.Amount && !!this._amount ||
                this._allocationType?.code === AllocationTypeEnum.Percentage && !!this._percentage);
    }

    public get label(): string {
        return this._label;
    }

    public set label(value: string) {
        const hasChanged = this._setValue('_label', value, 'string');
        if (hasChanged) {
            this.auto = false;
        }
    }

    public get percentage(): number {
        return this._percentage;
    }

    public set percentage(value: any) {
        // `precision: 5` : sans elle, `toFloat` arrondit à deux décimales et le pourcentage
        // ne peut pas porter le complément d'une répartition à parts égales — trois parts
        // retombaient à 33,33 %, soit 99,99 %, refusé par le serveur. Le champ de saisie
        // accepte cinq décimales, et l'analytique les stocke déjà (#1040)
        const hasChanged = this._setValue('_percentage', value, 'float', { precision: 5 });
        if (hasChanged) {
            this.auto = false;
        }
    }


}